<template>
  <div ref="root" class="track-add-menu">
    <button
      type="button"
      class="btn btn-icon"
      aria-label="Добавить трек"
      :aria-expanded="menuOpen"
      aria-haspopup="menu"
      :disabled="uploading"
      @click="toggleMenu"
    >
      <span aria-hidden="true" class="btn-icon-mark">+</span>
    </button>

    <ul v-if="menuOpen" class="track-add-menu__panel" role="menu">
      <li role="none">
        <router-link class="track-add-menu__action" :to="{ name: 'track-new' }" role="menuitem" @click="closeMenu">
          Добавить один
        </router-link>
      </li>
      <li role="none">
        <button type="button" class="track-add-menu__action" role="menuitem" @click="pickMultiple">
          Добавить несколько
        </button>
      </li>
    </ul>

    <input
      ref="fileInput"
      type="file"
      accept=".gpx,application/gpx+xml"
      multiple
      hidden
      :disabled="uploading"
      @change="onFilesSelected"
    />

    <p v-if="error" class="error-message track-add-menu__error">{{ error }}</p>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { uploadGpx } from '../api/tracks'

const MAX_FILES = 50

const emit = defineEmits(['uploaded'])

const root = ref(null)
const fileInput = ref(null)
const menuOpen = ref(false)
const uploading = ref(false)
const error = ref('')

function toggleMenu() {
  menuOpen.value = !menuOpen.value
}

function closeMenu() {
  menuOpen.value = false
}

function pickMultiple() {
  closeMenu()
  fileInput.value?.click()
}

async function onFilesSelected(event) {
  const files = Array.from(event.target.files || [])
  event.target.value = ''
  error.value = ''

  if (!files.length) return

  const gpxFiles = files.filter((file) => file.name.toLowerCase().endsWith('.gpx'))
  if (!gpxFiles.length) {
    error.value = 'Выберите GPX-файлы'
    return
  }

  if (gpxFiles.length > MAX_FILES) {
    error.value = `Можно выбрать не больше ${MAX_FILES} файлов за раз`
  }

  await uploadAll(gpxFiles.slice(0, MAX_FILES))
}

async function uploadAll(files) {
  if (uploading.value || !files.length) return

  uploading.value = true
  let failCount = 0

  for (const file of files) {
    try {
      const { data } = await uploadGpx(file, { public: false })
      emit('uploaded', data)
    } catch {
      failCount += 1
    }
  }

  if (failCount === files.length) {
    error.value = 'Не удалось загрузить ни один файл'
  } else if (failCount > 0) {
    error.value = `Не удалось загрузить ${failCount} из ${files.length} файлов`
  }

  uploading.value = false
}

function handleDocumentClick(event) {
  if (!root.value?.contains(event.target)) {
    closeMenu()
  }
}

onMounted(() => {
  document.addEventListener('click', handleDocumentClick)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick)
})
</script>

<style scoped>
.track-add-menu {
  position: relative;
}

.track-add-menu__panel {
  position: absolute;
  right: 0;
  top: calc(100% + 0.375rem);
  z-index: 20;
  margin: 0;
  padding: 0.375rem;
  list-style: none;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  background: #fff;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.08);
  min-width: 12rem;
}

.track-add-menu__action {
  display: block;
  width: 100%;
  border: 1px solid transparent;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: transparent;
  color: #334155;
  cursor: pointer;
  font-size: 0.875rem;
  text-align: left;
  text-decoration: none;
}

.track-add-menu__action:hover {
  background: #f8fafc;
}

.track-add-menu__error {
  position: absolute;
  right: 0;
  top: calc(100% + 0.375rem);
  z-index: 19;
  margin: 0;
  width: max-content;
  max-width: min(calc(100vw - 2.5rem), 20rem);
  white-space: normal;
}
</style>
